import React, { useEffect, useMemo, useState } from "react";

import { BackupIcon, InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { useTheme } from "styled-components";

import ContentLayout from "Components/ContentLayout";
import PageMeta from "Components/PageMeta";
import SubNavBar from "Components/SubNavBar";
import { ACTIVITY_CONTEXT, BACKUP_TYPES } from "Constants/constants";
import EnvironmentBackupModal from "Containers/NavBar/Environment/BackupModal";
import Button from "ds/Button";
import Dialog from "ds/Dialog";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { loadEnvironmentActivities } from "Reducers/activity";
import { getAllBackups } from "Reducers/backupsStored/thunks/getAllBackups.thunk";
import { loadEnvironment, environmentSelector } from "Reducers/environment";
import {
  selectSubscriptionDetails,
  loadingSelector
} from "Reducers/organization/subscription";
import { selectEnableCustomBackupSchedules } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import Illustration from "Vendor_resources/images/empty-backups.svg";
import IllustrationDark from "Vendor_resources/images/empty-backups_dark.svg";

import * as S from "./Backups.styles";

type NoBackupsIllustrationProps = {
  onClick: (e?: React.MouseEvent) => void;
};

export const NoBackupsIllustration = ({
  onClick
}: NoBackupsIllustrationProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const environment = useSelectorWithUrlParams(environmentSelector);

  return (
    <S.EmptyText className="empty-text">
      <S.ImageWrapper>
        <img
          src={theme.name === "dark" ? IllustrationDark : Illustration}
          alt={intl.formatMessage({ id: "environment.backups.empty.title" })}
        />
      </S.ImageWrapper>
      <S.NoBackupsTitle tag="h2">
        {intl.formatMessage({
          id: "environment.backups.empty.title",
          defaultMessage: "No backups"
        })}
      </S.NoBackupsTitle>
      <S.Paragraph>
        {intl.formatMessage({
          id: "environment.backups.empty.details",
          defaultMessage:
            "You have no recent or running backups on this environment."
        })}
      </S.Paragraph>
      <Button
        disabled={environment?.status === "inactive"}
        content="mixed"
        onClick={onClick}
        analyticId="backup"
      >
        <BackupIcon />
        {intl.formatMessage({ id: "backup" })}
      </Button>
    </S.EmptyText>
  );
};

type BackupsProps = {
  projectId: string;
  environmentId: string;
  organizationId: string;
  children: React.ReactNode;
};

const Backups = ({
  environmentId,
  organizationId,
  projectId,
  children
}: BackupsProps) => {
  const [openModal, setOpenModal] = useState(false);

  const dispatch = useAppDispatch();
  const intl = useIntl();

  const { pathname } = useLocation();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const [backupScheduleSettingsUrl] = useUrls([
    "organization.project.settings.backupSchedules"
  ]);

  const subscription = useAppSelector(state => {
    return selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId: project?.getSubscriptionId()
    });
  });

  const isLoadingSubscription = useAppSelector(loadingSelector);
  const isCustomBackupSchedulesEnabled = useSelectorWithUrlParams(
    selectEnableCustomBackupSchedules
  );

  useEffect(() => {
    dispatch(
      loadEnvironmentActivities({
        projectDescriptionId: projectId,
        environmentDescriptionId: environmentId,
        organizationDescriptionId: organizationId,
        context: ACTIVITY_CONTEXT.Environment,
        filterNames: ["backup"]
      })
    );
  }, [dispatch, environmentId, organizationId, projectId]);

  useEffect(() => {
    if (!environment)
      dispatch(loadEnvironment({ organizationId, projectId, environmentId }));
  }, [dispatch, environment, environmentId, organizationId, projectId]);

  useEffect(() => {
    dispatch(getAllBackups({ projectId, environmentId }));
  }, [
    dispatch,
    environmentId,
    projectId,
    // pathname is a dependency because the change can impact the "note" visibility in the BackupModal
    pathname
  ]);

  const tabs = useMemo(() => {
    const backupPath = `/${organizationId}/${projectId}/${encodeURIComponent(
      environmentId
    )}/backups`;
    return [
      {
        name: "backups_stored",
        route: backupPath,
        testPath: path => path == backupPath
      },
      {
        name: "backups_history",
        route: `${backupPath}/history`,
        testPath: path => path == `${backupPath}/history`
      }
    ];
  }, [environmentId, organizationId, projectId]);

  let tooltip: React.JSX.Element | null = null;
  if (isCustomBackupSchedulesEnabled) {
    tooltip = (
      <Dialog
        description={intl.formatMessage({
          id: "environment.backups.tooltip.custom_backup_scheduling.description"
        })}
        link={
          <Button
            variant="link"
            href={intl.formatMessage({
              id: "links.documentation.environments.backup"
            })}
            analyticId="learnmore"
          >
            {intl.formatMessage({
              id: "learnmore"
            })}
          </Button>
        }
        trigger={
          <S.IconControl aria-label="info-trigger-backups">
            <InfoSm />
          </S.IconControl>
        }
      />
    );
  } else if (environment?.type.match(/production/i)) {
    if (!isLoadingSubscription) {
      const backup = subscription?.backups;
      const defaultBackup =
        subscription?.project_options?.features?.backups?.default[0];

      const backupPlan: (typeof BACKUP_TYPES)[keyof typeof BACKUP_TYPES] =
        BACKUP_TYPES[backup! || defaultBackup!];

      if (backupPlan?.automated && backupPlan?.manual) {
        tooltip = (
          <Dialog
            analyticId="environment.backups.info.prod"
            description={intl.formatMessage<React.ReactNode>(
              {
                id: "environment.backups.info.prod"
              },
              {
                automatedAmount: backupPlan?.automated,
                manualAmount: backupPlan?.manual,
                strong: txt => <strong>{txt}</strong>
              }
            )}
            link={
              <Button
                variant="link"
                to={intl.formatMessage({
                  id: "links.documentation.admin_backups"
                })}
                analyticId="learnmore"
              >
                {intl.formatMessage({
                  id: "learnmore"
                })}
              </Button>
            }
            trigger={
              <S.IconControl aria-label="info-trigger-admin-backups">
                <InfoSm />
              </S.IconControl>
            }
          />
        );
      }
    }
  } else {
    tooltip = (
      <Dialog
        analyticId="environment.backups.info.dev"
        description={intl.formatMessage<React.ReactNode>(
          {
            id: "environment.backups.info.dev"
          },
          {
            amount: 2,
            strong: txt => <strong>{txt}</strong>
          }
        )}
        link={
          <Button
            variant="link"
            to={intl.formatMessage({
              id: "links.documentation.admin_backups"
            })}
            analyticId="learnmore"
          >
            {intl.formatMessage({
              id: "learnmore"
            })}
          </Button>
        }
        trigger={
          <S.IconControl aria-label="info-trigger-admin-backups-docu">
            <InfoSm />
          </S.IconControl>
        }
      />
    );
  }

  return (
    <ContentLayout>
      <PageMeta title={`Backups | ${environment?.title} | ${project?.title}`} />

      <S.Wrapper className="environment-backups">
        <S.Heading>
          <Typography.Title tag="h2">
            {intl.formatMessage({
              id: "environment.backups.title",
              defaultMessage: "Backups"
            })}
            {tooltip}
          </Typography.Title>
          {tabs && (
            <SubNavBar
              tabs={tabs}
              currentPathName={pathname}
              islightbackground
            />
          )}
          {isCustomBackupSchedulesEnabled && (
            <S.Button
              variant="outline"
              analyticId="environment.backups.btn.manage_schedule"
              to={backupScheduleSettingsUrl}
            >
              {intl.formatMessage({
                id: "environment.backups.btn.manage_schedule",
                defaultMessage: "Manage schedule"
              })}
            </S.Button>
          )}
        </S.Heading>
        {children}
      </S.Wrapper>

      <EnvironmentBackupModal
        isOpen={openModal}
        closeModal={() => setOpenModal(false)}
      />
    </ContentLayout>
  );
};

export default Backups;
